// src/pages/help/HelpCenter.tsx
// Centro de Ayuda principal - Página de inicio

import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { helpSections, faqItems, searchHelpContent, HelpArticle, FAQItem, HelpSection } from '../../data/helpCenterData';
import { useUser } from '../../context/UserContext';

import Navbar from '../../components/navbar/navbar';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import PageTitle from '../../components/page-title';
import SEO from '../../components/SEO';

export default function HelpCenter() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<(HelpArticle | FAQItem)[]>([]);
  const [showResults, setShowResults] = useState(false);

  // Verificar permisos del usuario
  const isAdmin = user?.rol === 'ADMIN';
  const isAdminComite = user?.esAdminComite === true;

  // Filtrar secciones según el rol del usuario
  const filteredSections = useMemo(() => {
    return helpSections.filter((section: HelpSection) => {
      // Ocultar guía de admin si no es admin
      if (section.id === 'admin-guide' && !isAdmin) {
        return false;
      }
      // Ocultar guía de comité si no es admin ni admin de comité
      if (section.id === 'committee-guide' && !isAdmin && !isAdminComite) {
        return false;
      }
      return true;
    });
  }, [isAdmin, isAdminComite]);

  // Filtrar resultados de búsqueda según permisos
  const filterSearchResults = (results: (HelpArticle | FAQItem)[]) => {
    return results.filter(item => {
      if ('content' in item) {
        // Es un artículo - verificar si pertenece a una sección restringida
        const articleId = item.id;

        // Buscar en qué sección está el artículo
        for (const section of helpSections) {
          const articleInSection = section.articles.find(a => a.id === articleId);
          if (articleInSection) {
            // Verificar acceso a la sección
            if (section.id === 'admin-guide' && !isAdmin) return false;
            if (section.id === 'committee-guide' && !isAdmin && !isAdminComite) return false;
            break;
          }
        }

        // También verificar rol del artículo individual
        if (item.role === 'admin' && !isAdmin) return false;
        if (item.role === 'comite' && !isAdmin && !isAdminComite) return false;
      }
      return true;
    });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const results = searchHelpContent(searchQuery);
      setSearchResults(filterSearchResults(results));
      setShowResults(true);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (value.trim().length >= 2) {
      const results = searchHelpContent(value);
      setSearchResults(filterSearchResults(results));
      setShowResults(true);
    } else {
      setShowResults(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  const isArticle = (item: HelpArticle | FAQItem): item is HelpArticle => {
    return 'content' in item;
  };

  // Obtener FAQs populares (primeras 4)
  const popularFAQs = faqItems.slice(0, 4);

  return (
    <>
      <SEO
        title="Centro de Ayuda"
        description="Centro de ayuda de AMPI Riviera Nayarit. Guías, tutoriales y respuestas para usar la plataforma inmobiliaria. Soporte para afiliados, asociados y agencias."
        path="/ayuda"
        keywords="ayuda AMPI, soporte plataforma inmobiliaria, guías AMPI riviera nayarit, tutoriales portal inmobiliario"
      />
      <Navbar transparent={false} />

      <PageTitle
        title="Centro de Ayuda"
        subtitle="Encuentra respuestas, guías y tutoriales para usar la plataforma"
      />

      {/* Hero de Búsqueda */}
      <section className="py-5 bg-primary">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 col-md-10">
              <div className="text-center mb-4">
                <h2 className="text-white mb-2">¿En qué podemos ayudarte?</h2>
                <p className="text-white-50">Busca en nuestra base de conocimiento</p>
              </div>

              <form onSubmit={handleSearch} className="position-relative">
                <div className="input-group input-group-lg">
                  <span className="input-group-text bg-white border-0">
                    <i className="fa-solid fa-search text-muted"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control border-0 py-3"
                    placeholder="Buscar artículos, guías, preguntas frecuentes..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="btn btn-light border-0"
                      onClick={clearSearch}
                    >
                      <i className="fa-solid fa-times"></i>
                    </button>
                  )}
                  <button type="submit" className="btn btn-dark px-4">
                    Buscar
                  </button>
                </div>

                {/* Resultados de búsqueda */}
                {showResults && (
                  <div className="position-absolute w-100 mt-2 bg-white rounded-3 shadow-lg" style={{ zIndex: 1000, maxHeight: '400px', overflowY: 'auto' }}>
                    {searchResults.length > 0 ? (
                      <div className="p-3">
                        <p className="text-muted small mb-2">
                          {searchResults.length} resultado{searchResults.length !== 1 ? 's' : ''} encontrado{searchResults.length !== 1 ? 's' : ''}
                        </p>
                        {searchResults.map((item, index) => (
                          <div
                            key={index}
                            className="p-3 border-bottom hover-bg-light cursor-pointer"
                            onClick={() => {
                              if (isArticle(item)) {
                                navigate(`/ayuda/articulo/${item.id}`);
                              } else {
                                navigate('/ayuda/faq');
                              }
                              clearSearch();
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="d-flex align-items-start">
                              <i className={`fa-solid ${isArticle(item) ? 'fa-file-lines' : 'fa-circle-question'} text-primary me-3 mt-1`}></i>
                              <div>
                                <h6 className="mb-1">
                                  {isArticle(item) ? item.title : item.question}
                                </h6>
                                <p className="text-muted small mb-0 text-truncate" style={{ maxWidth: '500px' }}>
                                  {isArticle(item)
                                    ? item.content.substring(0, 100).replace(/[#*`]/g, '') + '...'
                                    : item.answer.substring(0, 100) + '...'
                                  }
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 text-center">
                        <i className="fa-solid fa-search fa-2x text-muted mb-3"></i>
                        <p className="text-muted mb-0">No se encontraron resultados para "{searchQuery}"</p>
                        <p className="text-muted small">Intenta con otras palabras clave</p>
                      </div>
                    )}
                  </div>
                )}
              </form>

              <div className="text-center mt-3">
                <span className="text-white-50 small">Búsquedas populares: </span>
                <Link to="/ayuda/articulo/login-guide" className="badge bg-white text-primary me-2">Iniciar sesión</Link>
                <Link to="/ayuda/articulo/my-workshops" className="badge bg-white text-primary me-2">Mis talleres</Link>
                <Link to="/ayuda/articulo/edit-profile" className="badge bg-white text-primary me-2">Editar perfil</Link>
                <Link to="/ayuda/faq" className="badge bg-white text-primary">FAQ</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Secciones del Centro de Ayuda */}
      <section className="gray-simple py-5">
        <div className="container">
          <div className="row mb-4">
            <div className="col-12">
              <h3 className="mb-1">Categorías de Ayuda</h3>
              <p className="text-muted">Selecciona una categoría para explorar los artículos</p>
            </div>
          </div>

          <div className="row g-4">
            {filteredSections.filter(s => s.id !== 'faq').map((section) => (
              <div className="col-lg-3 col-md-6" key={section.id}>
                <Link to={`/ayuda/seccion/${section.id}`} className="text-decoration-none">
                  <div className="card h-100 border-0 shadow-sm hover-shadow transition-all">
                    <div className="card-body text-center p-4">
                      <div className="mb-3">
                        <span className="d-inline-flex align-items-center justify-content-center bg-primary bg-opacity-10 rounded-circle" style={{ width: '70px', height: '70px' }}>
                          <i className={`${section.icon} fa-2x text-primary`}></i>
                        </span>
                      </div>
                      <h5 className="card-title text-dark">{section.title}</h5>
                      <p className="card-text text-muted small">{section.description}</p>
                      <span className="badge bg-light text-muted">
                        {section.articles.length} artículo{section.articles.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
            ))}

            {/* Tarjeta especial de FAQ */}
            <div className="col-lg-3 col-md-6">
              <Link to="/ayuda/faq" className="text-decoration-none">
                <div className="card h-100 border-0 shadow-sm hover-shadow transition-all bg-primary">
                  <div className="card-body text-center p-4">
                    <div className="mb-3">
                      <span className="d-inline-flex align-items-center justify-content-center bg-white rounded-circle" style={{ width: '70px', height: '70px' }}>
                        <i className="fa-solid fa-circle-question fa-2x text-primary"></i>
                      </span>
                    </div>
                    <h5 className="card-title text-white">Preguntas Frecuentes</h5>
                    <p className="card-text text-white-50 small">Respuestas rápidas a las dudas más comunes</p>
                    <span className="badge bg-white text-primary">
                      {faqItems.length} preguntas
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Populares */}
      <section className="py-5">
        <div className="container">
          <div className="row mb-4">
            <div className="col-lg-8">
              <h3 className="mb-1">Preguntas Populares</h3>
              <p className="text-muted">Las consultas más frecuentes de nuestros usuarios</p>
            </div>
            <div className="col-lg-4 text-lg-end">
              <Link to="/ayuda/faq" className="btn btn-outline-primary">
                Ver todas las FAQ <i className="fa-solid fa-arrow-right ms-2"></i>
              </Link>
            </div>
          </div>

          <div className="row g-4">
            {popularFAQs.map((faq) => (
              <div className="col-lg-6" key={faq.id}>
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="d-flex">
                      <div className="flex-shrink-0">
                        <span className="d-inline-flex align-items-center justify-content-center bg-primary bg-opacity-10 rounded-circle" style={{ width: '40px', height: '40px' }}>
                          <i className="fa-solid fa-question text-primary"></i>
                        </span>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h6 className="mb-2">{faq.question}</h6>
                        <p className="text-muted small mb-0">{faq.answer}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Guías por Rol */}
      <section className="gray-simple py-5">
        <div className="container">
          <div className="row mb-4">
            <div className="col-12 text-center">
              <h3 className="mb-1">Guías por Tipo de Usuario</h3>
              <p className="text-muted">Selecciona tu rol para ver el manual específico</p>
            </div>
          </div>

          <div className="row g-4 justify-content-center">
            {/* Guía de Administrador - Solo visible para admins */}
            {isAdmin && (
              <div className="col-lg-3 col-md-6">
                <Link to="/ayuda/seccion/admin-guide" className="text-decoration-none">
                  <div className="card border-0 shadow-sm text-center p-4 h-100 hover-shadow border-danger">
                    <i className="fa-solid fa-user-shield fa-3x text-danger mb-3"></i>
                    <h5 className="text-dark">Administrador</h5>
                    <p className="text-muted small mb-0">Control total del sistema</p>
                    <span className="badge bg-danger mt-2">Solo Admin</span>
                  </div>
                </Link>
              </div>
            )}

            {/* Guía de Comité - Solo visible para admins y admin de comité */}
            {(isAdmin || isAdminComite) && (
              <div className="col-lg-3 col-md-6">
                <Link to="/ayuda/seccion/committee-guide" className="text-decoration-none">
                  <div className="card border-0 shadow-sm text-center p-4 h-100 hover-shadow border-warning">
                    <i className="fa-solid fa-calendar-check fa-3x text-warning mb-3"></i>
                    <h5 className="text-dark">Comité de Eventos</h5>
                    <p className="text-muted small mb-0">Gestión de capacitación</p>
                    <span className="badge bg-warning text-dark mt-2">Comité</span>
                  </div>
                </Link>
              </div>
            )}

            {/* Guías públicas para todos */}
            <div className="col-lg-3 col-md-6">
              <Link to="/ayuda/seccion/member-guide" className="text-decoration-none">
                <div className="card border-0 shadow-sm text-center p-4 h-100 hover-shadow">
                  <i className="fa-solid fa-user-tie fa-3x text-primary mb-3"></i>
                  <h5 className="text-dark">Asesor (Afiliado)</h5>
                  <p className="text-muted small mb-0">Propiedades y talleres</p>
                </div>
              </Link>
            </div>

            <div className="col-lg-3 col-md-6">
              <Link to="/ayuda/seccion/member-guide" className="text-decoration-none">
                <div className="card border-0 shadow-sm text-center p-4 h-100 hover-shadow">
                  <i className="fa-solid fa-user fa-3x text-success mb-3"></i>
                  <h5 className="text-dark">Asociado</h5>
                  <p className="text-muted small mb-0">Perfil y talleres</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Banner de Contacto */}
      <section className="py-5 bg-dark">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <h4 className="text-white mb-2">¿No encontraste lo que buscabas?</h4>
              <p className="text-white-50 mb-0">Nuestro equipo de soporte está listo para ayudarte</p>
            </div>
            <div className="col-lg-4 text-lg-end mt-3 mt-lg-0">
              <Link to="/contact" className="btn btn-primary btn-lg">
                <i className="fa-solid fa-envelope me-2"></i>
                Contactar Soporte
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Glosario Preview */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <h3 className="mb-1">Glosario de Términos</h3>
              <p className="text-muted">Conoce los términos más utilizados en la plataforma</p>
            </div>
            <div className="col-lg-4 text-lg-end">
              <Link to="/ayuda/glosario" className="btn btn-outline-secondary">
                Ver glosario completo <i className="fa-solid fa-arrow-right ms-2"></i>
              </Link>
            </div>
          </div>

          <div className="row mt-4">
            <div className="col-12">
              <div className="d-flex flex-wrap gap-2">
                {['Afiliado', 'Asociado', 'Créditos AMPI', 'Dashboard', 'Lead', 'QR de Asistencia', 'Taller'].map((term) => (
                  <Link
                    key={term}
                    to="/ayuda/glosario"
                    className="badge bg-light text-dark border px-3 py-2 text-decoration-none hover-shadow"
                  >
                    {term}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />

      {/* Estilos adicionales */}
      <style>{`
        .hover-shadow:hover {
          transform: translateY(-2px);
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
        }
        .transition-all {
          transition: all 0.3s ease;
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .hover-bg-light:hover {
          background-color: #f8f9fa;
        }
      `}</style>
    </>
  );
}
